import type Accordion from './Accordion.js'
import { LitElement } from 'lit'

import { customElement, property } from 'lit/decorators.js'
import { FocusableWhenDisabledController } from '../common/controllers/FocusableWhenDisabledController.js'
import { observe } from '../common/decorators/observe.js'

import { injectLegacyLightDomStyles } from '../common/light-dom-styles.js'
import style from './AccordionTrigger.css'

/**
 * Accordion Trigger is the button that opens and closes its
 * [Accordion Panel](/components/accordion-panel/). It must be placed inside an
 * [Accordion Header](/components/accordion-header/).
 *
 * @status draft
 * @category container
 * @lightDom true
 *
 * @slot - Default slot for the trigger label and an icon. The icon is the last
 * child, so any element or web component can be used; set its size on the
 * element itself (for example `<nord-icon size="xxs">`).
 *
 * @cssprop [--n-accordion-trigger-gap=var(--n-space-xs)] - The gap between the label and the icon, using our [spacing tokens](/tokens/#space).
 */
@customElement('nord-accordion-trigger')
export default class AccordionTrigger extends LitElement {
  /**
   * Whether the associated panel is open. Set by the parent
   * [Accordion Item](/components/accordion-item/); reflected so it can be styled.
   */
  @property({ reflect: true, type: Boolean }) open = false

  /**
   * Whether the trigger is disabled. Set by the parent
   * [Accordion Item](/components/accordion-item/).
   */
  @property({ reflect: true, type: Boolean }) disabled = false

  /**
   * How to justify the label and icon along the trigger. Defaults to
   * `space-between`, pushing the icon to the far end; set `start` to sit the
   * label and icon next to each other.
   */
  @property({ reflect: true, attribute: 'justify-content' }) justifyContent?:
    | 'start'
    | 'center'
    | 'end'
    | 'space-between'
    | 'space-around'
    | 'space-evenly'

  // Keeps the trigger focusable while disabled (aria-disabled + tabindex=0),
  // matching Base UI; activation is blocked by `activate()` below.
  private focusable = new FocusableWhenDisabledController(this, { disabled: () => this.disabled })

  connectedCallback() {
    super.connectedCallback()
    injectLegacyLightDomStyles(this, style)
    // The host itself is the focusable button: light DOM can't delegate focus
    // into a shadow `<button>`, so we apply button semantics directly.
    this.setAttribute('role', 'button')
    this.setAttribute('aria-expanded', `${this.open}`)
    this.addEventListener('click', this.handleClick)
    this.addEventListener('keydown', this.handleKeydown)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListener('click', this.handleClick)
    this.removeEventListener('keydown', this.handleKeydown)
  }

  // Render in the light DOM so the label/icon stay the element's own content and
  // the injected stylesheet applies (no shadow boundary, like Table).
  protected createRenderRoot() {
    return this
  }

  private get accordion() {
    return this.closest<Accordion>('nord-accordion')
  }

  @observe('open')
  protected handleOpenChange() {
    this.setAttribute('aria-expanded', `${this.open}`)
  }

  private activate() {
    if (this.disabled) {
      return
    }

    // Call the parent Accordion directly (no bubbling event), mirroring how
    // CollapsibleTrigger calls its Collapsible.
    this.accordion?.activateTrigger(this)
  }

  private handleClick = () => {
    this.activate()
  }

  private handleKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      this.activate()
      return
    }

    this.accordion?.handleTriggerKeydown(event)
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-accordion-trigger': AccordionTrigger
  }
}
